// Mirrors of src/libraries/PnlMath.sol and the keeper's open-interest cap policy.
import type { Address } from 'viem';

import type { CapPolicy, HouseLendPolicy, PoolPolicy, ReservePolicy } from './config.ts';

const WAD = 10n ** 18n;
const BPS = 10_000n;
/** Wins count from `entry / DEAD_ZONE_DIVISOR` ticks past the entry: 2 bp (PnlMath.DEAD_ZONE_DIVISOR) */
export const DEAD_ZONE_DIVISOR = 5_000n;
/** The reference deployment's 0.2 bp, for the mainnet replay vectors */
export const REFERENCE_DEAD_ZONE_DIVISOR = 50_000n;

/**
 * PnL of a position closed at `exitPx` (PnlMath.pnl): `gross` is the linear PnL net of the dead zone,
 * `credited` what settles (equal on a loss, shrunk by the payout curve on a win). Fees not included.
 */
export function pnl(
  p: { entryPx: bigint; exitPx: bigint; margin: bigint; leverage: bigint; isLong: boolean },
  curve: { winHaircut: bigint; curveA: bigint; curveB: bigint },
  deadZoneDivisor: bigint = DEAD_ZONE_DIVISOR,
): { gross: bigint; credited: bigint } {
  const notional = p.margin * p.leverage;
  const up = p.exitPx >= p.entryPx;
  const move = up ? p.exitPx - p.entryPx : p.entryPx - p.exitPx;
  if (up !== p.isLong || move === 0n) {
    const loss = -((notional * move) / p.entryPx);
    return { gross: loss, credited: loss };
  }
  const deadZone = p.entryPx / deadZoneDivisor;
  if (move <= deadZone) return { gross: 0n, credited: 0n };
  const win = (notional * (move - deadZone)) / p.entryPx;
  const x = (win * WAD) / notional;
  if (x === 0n) return { gross: win, credited: 0n };
  const r = WAD ** 3n / (curve.curveA * x) + (curve.winHaircut * WAD ** 2n) / (curve.curveB * x);
  const g = ((WAD - curve.winHaircut) * WAD) / (WAD + r);
  return { gross: win, credited: (win * g) / WAD };
}

export type MintCurve = {
  tailProgress: bigint;
  flatThreshold: bigint;
  flatRate: bigint;
  tailK: bigint;
  /**
   * Multiple of the curve's rate at an empty house (PapurrTokenomics.lowHouseMultiple, WAD): under the threshold
   * the rate ramps from x1 at the threshold to this, capped at the flat rate. Absent on a tokenomics from before
   * it, which mints the flat rate itself there.
   */
  lowHouseMultiple?: bigint;
};

/**
 * PAPURR minted for `amount` USD (MintMath.mintAmount): the boosted curve rate up to the house threshold
 * (`lowHouseRate`), then flatRate * x * K^2 / ((K+T)(K+T+x)). Same rounding as the contract (mulDiv floors).
 */
export function mintAmount(p: MintCurve & {
  lp: bigint;
  amount: bigint;
  /** Soft-launch rate (PapurrTokenomics.fixedRate): while non zero, this much per USD and no tail */
  fixedRate?: bigint;
}): { minted: bigint; tailAdvance: bigint } {
  if (p.fixedRate !== undefined && p.fixedRate > 0n) return { minted: (p.amount * p.fixedRate) / WAD, tailAdvance: 0n };
  let minted = 0n;
  let flatPart = 0n;
  if (p.lp < p.flatThreshold) {
    const room = p.flatThreshold - p.lp;
    flatPart = p.amount < room ? p.amount : room;
    minted = (flatPart * lowHouseRate(p, room)) / WAD;
  }
  const tailAdvance = p.amount - flatPart;
  if (tailAdvance === 0n) return { minted, tailAdvance: 0n };
  const k0 = p.tailK + p.tailProgress;
  const tail = (((tailAdvance * p.flatRate) / WAD) * p.tailK) / k0;
  minted += (tail * p.tailK) / (k0 + tailAdvance);
  return { minted, tailAdvance };
}

/**
 * PAPURR per USD under the threshold (MintMath.lowHouseRate): the curve's rate at T, times 1 + (multiple - 1) x
 * (`deficit` under the threshold as a share of it, at most 1), capped at the flat rate
 */
export function lowHouseRate(p: MintCurve, deficit: bigint): bigint {
  if (p.lowHouseMultiple === undefined) return p.flatRate;
  const k0 = p.tailK + p.tailProgress;
  let rate = (((p.flatRate * p.tailK) / k0) * p.tailK) / k0;
  if (p.lowHouseMultiple > WAD) {
    const frac = deficit >= p.flatThreshold ? WAD : (deficit * WAD) / p.flatThreshold;
    rate = (rate * (WAD + ((p.lowHouseMultiple - WAD) * frac) / WAD)) / WAD;
  }
  return rate > p.flatRate ? p.flatRate : rate;
}

/** Fee taken on a settled amount (PnlMath.feeSplit). */
/**
 * What the house keeps of a win closed within `bandBps` of the entry (PnlMath.nearEntryCut): `maxCut` (WAD) of
 * it at the entry, tapering linearly to nothing at the band's edge
 */
export function nearEntryCut(win: bigint, entryPx: bigint, exitPx: bigint, bandBps: bigint, maxCut: bigint): bigint {
  if (win === 0n || bandBps === 0n || maxCut === 0n) return 0n;
  const move = exitPx > entryPx ? exitPx - entryPx : entryPx - exitPx;
  let band = (entryPx * bandBps) / BPS;
  if (band === 0n) band = 1n;
  if (move >= band) return 0n;
  return (((win * maxCut) / WAD) * (band - move)) / band;
}

export function fee(amount: bigint, rate: bigint): bigint {
  return (amount * rate) / WAD;
}

/** Price at which a position is liquidated (PnlMath.bustPrice). */
export function bustPrice(entryPx: bigint, leverage: bigint, isLong: boolean, bustBuffer: bigint): bigint {
  if (isLong) {
    const base = entryPx - entryPx / leverage;
    return base + (base * bustBuffer) / WAD;
  }
  const base = entryPx + entryPx / leverage;
  return base - (base * bustBuffer) / WAD;
}

/** Fill band of a market (PapurrExchange.priceBand): the book mid and the mark, lower first */
export type Band = { low: bigint; high: bigint };

/** The price a trader gets: buys (long opens, short closes) at the top of the band, sells at the bottom */
export function fillPx(band: Band, buy: boolean): bigint {
  return buy ? band.high : band.low;
}

/**
 * Liquidation needs the side of the band most favourable to the trader past the bust price (a long's high,
 * a short's low), as in PapurrExchange._tryLiquidate: a pushed book alone does not liquidate.
 */
export function isLiquidatable(band: Band, bust: bigint, isLong: boolean): boolean {
  return isLong ? band.high <= bust : band.low >= bust;
}

/** USD (18 dec) to base-asset units (18 dec) at `mid` oracle units, `priceScale` units per USD. */
export function usdToBase(usd: bigint, mid: bigint, priceScale: bigint): bigint {
  return (usd * priceScale) / mid;
}

/**
 * Per-side open interest caps. Each side may exceed the other by the net exposure the protocol accepts,
 * `exposureBps` of its backing, clamped to [minCapUsd, hardCapUsd]. The backing is the house's effective
 * liquidity plus `reserveBps` of the reserve's capital (`reserveUsd`, see reserveBackingUsd): that capital
 * reaches the house within a few blocks when it owes (HouseReserve.backstop), so it carries the same risk. A
 * house that owes (negative liquidity) eats into the reserve first.
 */
export function oiCaps(
  p: { longOi: bigint; shortOi: bigint; mid: bigint; priceScale: bigint; effectiveLp: bigint; reserveUsd?: bigint },
  policy: CapPolicy,
): { longCap: bigint; shortCap: bigint } {
  const backing = p.effectiveLp + ((p.reserveUsd ?? 0n) * (policy.reserveBps ?? BPS)) / BPS;
  const lp = backing > 0n ? backing : 0n;
  const net = usdToBase((lp * policy.exposureBps) / BPS, p.mid, p.priceScale);
  const min = usdToBase(policy.minCapUsd, p.mid, p.priceScale);
  const max = usdToBase(policy.hardCapUsd, p.mid, p.priceScale);
  const clamp = (v: bigint) => (v < min ? min : v > max ? max : v);
  return { longCap: clamp(p.shortOi + net), shortCap: clamp(p.longOi + net) };
}

/** Whether `next` moved more than `bps` relative to `prev` (any move away from 0 counts). */
export function changedBeyond(prev: bigint, next: bigint, bps: bigint): boolean {
  if (prev === 0n) return next !== 0n;
  const diff = prev > next ? prev - next : next - prev;
  return diff * BPS > prev * bps;
}

/**
 * Staker share of the house's gain the next `distribute()` takes (PapurrExchange._takeGains): `newHighShare`
 * of what the house holds above its record (`peakExcess`, measured above `threshold`), nothing below it.
 */
export function gainShare(p: { effectiveLp: bigint; threshold: bigint; peakExcess: bigint; newHighShare: bigint }): bigint {
  const excess = p.effectiveLp > p.threshold ? p.effectiveLp - p.threshold : 0n;
  return excess > p.peakExcess ? ((excess - p.peakExcess) * p.newHighShare) / WAD : 0n;
}

/**
 * Spot the HouseReserve keeps unlent (USD, 18 dec): what a `stressBps` move on the house's net exposure
 * would cost beyond the house's own effective liquidity, never below `minBufferUsd`.
 */
export function reserveBuffer(netExposureUsd: bigint, effectiveLp: bigint, policy: ReservePolicy): bigint {
  const lp = effectiveLp > 0n ? effectiveLp : 0n;
  const stress = (netExposureUsd * policy.stressBps) / BPS;
  const beyond = stress > lp ? stress - lp : 0n;
  return beyond > policy.minBufferUsd ? beyond : policy.minBufferUsd;
}

/**
 * The reserve's capital the caps count as backing, USD (18 dec): its spot, plus what it has lent as far as
 * the lending market could return it now (its cash). Both reach the house within a few blocks when it owes:
 * `backstop` sends the spot and redeems the rest in the same call. Lent money beyond the market's cash
 * would wait for borrowers to repay, so it does not count.
 */
export function reserveBackingUsd(p: { spotUsd: bigint; suppliedUsd: bigint; cashUsd: bigint }): bigint {
  return p.spotUsd + (p.suppliedUsd < p.cashUsd ? p.suppliedUsd : p.cashUsd);
}

/**
 * Next lending move of the HouseReserve when the house owes nothing, all amounts in USD (18 dec):
 * redeem everything the market can return once it is `exitUtilBps` used, top the spot back up to its
 * target below the band, lend the excess above it. `floorUsd` is the contract's own unlent minimum.
 */
export function reserveMove(
  p: {
    spotUsd: bigint;
    suppliedUsd: bigint;
    cashUsd: bigint;
    utilizationBps: bigint;
    maxUtilizationBps: bigint;
    floorUsd: bigint;
    bufferUsd: bigint;
  },
  policy: ReservePolicy,
): { kind: 'supply' | 'redeem'; usd: bigint } | undefined {
  const min = (a: bigint, b: bigint) => (a < b ? a : b);
  const redeemable = min(p.suppliedUsd, p.cashUsd);
  if (p.utilizationBps >= policy.exitUtilBps) return redeemable > 0n ? { kind: 'redeem', usd: redeemable } : undefined;

  const target = p.bufferUsd > p.floorUsd ? p.bufferUsd : p.floorUsd;
  if (p.spotUsd * BPS < target * (BPS - policy.bandBps)) {
    const usd = min(target - p.spotUsd, redeemable);
    // a last remainder below the minimum still comes back, or it would sit lent for good
    return usd > 0n && (usd >= policy.minMoveUsd || usd === p.suppliedUsd) ? { kind: 'redeem', usd } : undefined;
  }
  if (p.spotUsd * BPS > target * (BPS + policy.bandBps) && p.utilizationBps <= p.maxUtilizationBps) {
    const usd = p.spotUsd - target;
    return usd >= policy.minMoveUsd ? { kind: 'supply', usd } : undefined;
  }
  return undefined;
}

/**
 * Next house lending move when the house owes nothing, all amounts in USD (18 dec). The spot the withdrawals
 * need comes first: below its target minus the band, lent money comes back whatever the share lent. Then what
 * is lent stays between `minLentBps` and `maxLentBps` of the house: out of that band, it goes back to the
 * middle (never past the contract's cap; a lend only from the spot above its target). Everything the market
 * can return comes back once it is `exitUtilBps` used.
 */
export function houseLendMove(
  p: {
    houseUsd: bigint;
    lentUsd: bigint;
    capUsd: bigint;
    spotUsd: bigint;
    spotTargetUsd: bigint;
    cashUsd: bigint;
    utilizationBps: bigint;
  },
  policy: HouseLendPolicy,
): { kind: 'supply' | 'redeem'; usd: bigint } | undefined {
  const min = (a: bigint, b: bigint) => (a < b ? a : b);
  const redeemable = min(p.lentUsd, p.cashUsd);
  // a last remainder below the minimum still comes back, or it would sit lent for good
  const redeem = (usd: bigint) =>
    usd > 0n && (usd >= policy.minMoveUsd || usd === p.lentUsd) ? { kind: 'redeem' as const, usd } : undefined;
  if (p.utilizationBps >= policy.exitUtilBps) return redeem(redeemable);
  if (p.spotUsd * BPS < p.spotTargetUsd * (BPS - policy.bandBps)) return redeem(min(p.spotTargetUsd - p.spotUsd, redeemable));

  const high = min((p.houseUsd * policy.maxLentBps) / BPS, p.capUsd);
  const middle = min((p.houseUsd * (policy.minLentBps + policy.maxLentBps)) / (2n * BPS), p.capUsd);
  if (p.lentUsd > high) return redeem(min(p.lentUsd - middle, redeemable));
  if (p.lentUsd < (p.houseUsd * policy.minLentBps) / BPS && p.utilizationBps <= policy.maxUtilBps) {
    const spare = p.spotUsd > p.spotTargetUsd ? p.spotUsd - p.spotTargetUsd : 0n;
    const usd = min(middle > p.lentUsd ? middle - p.lentUsd : 0n, spare);
    return usd >= policy.minMoveUsd && usd > 0n ? { kind: 'supply', usd } : undefined;
  }
  return undefined;
}

/**
 * The keeper's move between the exchange's two halves (config.PoolPolicy): toward a share of everything on
 * HyperEVM once that half is out of its band. A move to HyperEVM takes only the spot over the contract's floor
 * (`coreSpareUsd`); what the house has lent comes back through house lending, not here.
 */
export function poolMove(
  p: { evmUsd: bigint; coreSpotUsd: bigint; lentUsd: bigint; coreSpareUsd: bigint },
  policy: PoolPolicy,
): { kind: 'toCore' | 'toEvm'; usd: bigint } | undefined {
  const total = p.evmUsd + p.coreSpotUsd + p.lentUsd;
  let target = (total * policy.evmShareBps) / BPS;
  // the floor never takes more than half: a small exchange keeps both routes open
  if (target < policy.minEvmUsd) target = policy.minEvmUsd < total / 2n ? policy.minEvmUsd : total / 2n;
  if (p.evmUsd * BPS > target * (BPS + policy.bandBps)) {
    const usd = p.evmUsd - target;
    return usd >= policy.minMoveUsd && usd > 0n ? { kind: 'toCore', usd } : undefined;
  }
  if (p.evmUsd * BPS < target * (BPS - policy.bandBps)) {
    const want = target - p.evmUsd;
    const usd = want < p.coreSpareUsd ? want : p.coreSpareUsd;
    return usd >= policy.minMoveUsd && usd > 0n ? { kind: 'toEvm', usd } : undefined;
  }
  return undefined;
}

/**
 * Stakers whose unclaimed HYPE `PapurrStaking.sweep` would take now: no stake, unstake or claim for
 * `periodSec`, and at least `minWei` pending (core wei)
 */
export function idleSweepable(
  accounts: readonly { user: Address; lastActive: bigint; pending: bigint }[],
  nowSec: bigint,
  periodSec: bigint,
  minWei: bigint,
): Address[] {
  return accounts.filter((a) => a.pending >= minWei && a.pending > 0n && nowSec >= a.lastActive + periodSec).map((a) => a.user);
}

/** Net open interest of one market in USD (18 dec): what the house loses per unit of price move */
export function netExposureUsd(longOi: bigint, shortOi: bigint, mid: bigint, priceScale: bigint): bigint {
  const net = longOi > shortOi ? longOi - shortOi : shortOi - longOi;
  return (net * mid) / priceScale;
}
